import {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {questionsApi} from '../services/api';

const remaining = (limit, used) => Number(limit) - Number(used || 0);

export const calculateEngineUsage = (usage) => {
    if (!usage) return {availableQuestions: 0, concurrencyBlocked: false};
    if (usage.exempt) return {availableQuestions: Number.POSITIVE_INFINITY, concurrencyBlocked: false};
    if (!usage.allowed) return {availableQuestions: 0, concurrencyBlocked: false};
    const {limits = {}, usage: user = {}, organization_usage: organization = null} = usage;
    const candidates = [
        Number(limits.max_units_per_request),
        remaining(limits.daily_user_units, user.daily_user_units),
        remaining(limits.monthly_user_units, user.monthly_user_units),
    ];
    if (organization) {
        candidates.push(remaining(limits.daily_organization_units, organization.daily_units));
        candidates.push(remaining(limits.monthly_organization_units, organization.monthly_units));
    }
    const finiteCandidates = candidates.filter(Number.isFinite);
    const availableQuestions = Math.max(0, finiteCandidates.length ? Math.min(...finiteCandidates) : 0);
    const concurrencyBlocked = Number(user.concurrent_user || 0) >= Number(limits.max_concurrent_user)
        || Boolean(organization && Number(organization.concurrent || 0) >= Number(limits.max_concurrent_organization));
    return {availableQuestions, concurrencyBlocked};
};

export default function useEngineUsage(resourceId = '') {
    const [usage, setUsage] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const pendingRefresh = useRef(null);
    const currentResourceId = useRef(resourceId);
    currentResourceId.current = resourceId;

    const refresh = useCallback(() => {
        if (pendingRefresh.current?.resourceId === resourceId) return pendingRefresh.current.promise;
        setLoading(true);
        setError(null);
        const promise = questionsApi.usage(resourceId ? {resource_id: resourceId} : {})
            .then((result) => {
                if (currentResourceId.current === resourceId) setUsage(result);
                return result;
            })
            .catch((requestError) => {
                if (currentResourceId.current === resourceId) setError(requestError);
                return null;
            })
            .finally(() => {
                if (pendingRefresh.current?.promise === promise) pendingRefresh.current = null;
                if (currentResourceId.current === resourceId) setLoading(false);
            });
        pendingRefresh.current = {resourceId, promise};
        return promise;
    }, [resourceId]);

    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => {
        const handleFocus = () => refresh();
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [refresh]);

    return {...useMemo(() => calculateEngineUsage(usage), [usage]), usage, loading, error, refresh};
}
